import { useState, useEffect, useCallback } from 'react'
import { MagnifyingGlass } from '@phosphor-icons/react'
import { useQuery } from '@tanstack/react-query'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { orgApi } from '@/api/organizations'

interface OrgFiltersProps {
  search: string
  onSearchChange: (value: string) => void
  verticalSlug: string | undefined
  onVerticalChange: (value: string | undefined) => void
  isActive: boolean | null
  onStatusChange: (value: boolean | null) => void
  subscriptionTier: string | undefined
  onTierChange: (value: string | undefined) => void
}

export function OrgFilters({
  search,
  onSearchChange,
  verticalSlug,
  onVerticalChange,
  isActive,
  onStatusChange,
  subscriptionTier,
  onTierChange,
}: OrgFiltersProps) {
  const [localSearch, setLocalSearch] = useState(search)

  const { data: verticals } = useQuery({
    queryKey: ['verticals'],
    queryFn: () => orgApi.listVerticals(),
    staleTime: 10 * 60 * 1000,
  })

  // Debounced search -- 300ms
  const debouncedOnSearchChange = useCallback(
    (value: string) => {
      onSearchChange(value)
    },
    [onSearchChange]
  )

  useEffect(() => {
    const timer = setTimeout(() => {
      debouncedOnSearchChange(localSearch)
    }, 300)
    return () => clearTimeout(timer)
  }, [localSearch, debouncedOnSearchChange])

  // Sync external search prop back to local state
  useEffect(() => {
    setLocalSearch(search)
  }, [search])

  return (
    <div className="flex items-center gap-3">
      <div className="relative flex-1 max-w-sm">
        <MagnifyingGlass className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search organizations..."
          value={localSearch}
          onChange={(e) => setLocalSearch(e.target.value)}
          className="pl-9 h-9"
        />
      </div>

      <Select
        value={verticalSlug ?? '__all__'}
        onValueChange={(v) => onVerticalChange(v === '__all__' ? undefined : v)}
      >
        <SelectTrigger className="w-[160px] h-9">
          <SelectValue placeholder="All Verticals" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__all__">All Verticals</SelectItem>
          {verticals?.map((v) => (
            <SelectItem key={v.slug} value={v.slug}>
              {v.display_name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={isActive === null ? '__all__' : isActive ? 'active' : 'inactive'}
        onValueChange={(v) => {
          if (v === '__all__') onStatusChange(null)
          else if (v === 'active') onStatusChange(true)
          else onStatusChange(false)
        }}
      >
        <SelectTrigger className="w-[140px] h-9">
          <SelectValue placeholder="All Status" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__all__">All Status</SelectItem>
          <SelectItem value="active">Active</SelectItem>
          <SelectItem value="inactive">Inactive</SelectItem>
        </SelectContent>
      </Select>

      <Select
        value={subscriptionTier ?? '__all__'}
        onValueChange={(v) => onTierChange(v === '__all__' ? undefined : v)}
      >
        <SelectTrigger className="w-[130px] h-9">
          <SelectValue placeholder="All Tiers" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__all__">All Tiers</SelectItem>
          <SelectItem value="free">Free</SelectItem>
          <SelectItem value="pro">Pro</SelectItem>
          <SelectItem value="enterprise">Enterprise</SelectItem>
        </SelectContent>
      </Select>
    </div>
  )
}
